// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Generate HTML template for printing report content
 */
export function generatePrintHTML(title: string, content: string): string {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          body {
            font-family: system-ui, -apple-system, sans-serif;
            margin: 2rem;
            color: #111827;
            background: white;
            line-height: 1.5;
            min-height: 100vh;
            box-sizing: border-box;
          }
          
          html, body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
          }
          
          body {
            padding: 2rem;
          }
          h1, h2, h3, h4 {
            color: #111827;
            margin-bottom: 0.5rem;
          }
          h1 {
            font-size: 1.5rem;
            border-bottom: 2px solid #000;
            padding-bottom: 0.5rem;
            margin-bottom: 1rem;
          }
          .space-y-6 > * + * {
            margin-top: 1.5rem;
          }
          .space-y-4 > * + * {
            margin-top: 1rem;
          }
          .space-y-3 > * + * {
            margin-top: 0.75rem;
          }
          .space-y-2 > * + * {
            margin-top: 0.5rem;
          }
          .shadow-sm {
            border: 1px solid #ddd;
            border-radius: 0.5rem;
            padding: 1rem;
            margin-bottom: 1rem;
            background: white;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .bg-green-100 {
            background-color: #dcfce7;
            border: 1px solid #16a34a;
            color: #14532d;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            display: inline-block;
          }
          .bg-red-100 {
            background-color: #fee2e2;
            border: 1px solid #dc2626;
            color: #7f1d1d;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            display: inline-block;
          }
          .bg-blue-100 {
            background-color: #dbeafe;
            border: 1px solid #2563eb;
            color: #1e3a8a;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            display: inline-block;
          }
          .bg-gray-100 {
            background-color: #f3f4f6;
            border: 1px solid #6b7280;
            color: #374151;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            display: inline-block;
          }
          .bg-gray-50 {
            background-color: #f9fafb;
            border: 1px solid #d1d5db;
            padding: 0.75rem;
            border-radius: 0.5rem;
          }
          .text-muted-foreground {
            color: #6b7280;
          }
          .text-foreground {
            color: #111827;
          }
          ul, ol {
            padding-left: 1.5rem;
          }
          li {
            margin-bottom: 0.25rem;
          }
          @media print {
            @page {
              margin: 0.75in;
              size: A4;
            }
            
            /* Prevent sections (cards) from being split across pages */
            .shadow-sm,
            .rounded-xl,
            .shadow,
            [class*="shadow"] {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              page-break-before: auto;
              break-before: auto;
            }
            
            /* Ensure proper spacing between sections */
            .space-y-6 > *,
            .grid > * {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Keep card headers with their content */
            .flex.flex-col.space-y-1\\.5,
            .pb-3.p-4 {
              page-break-after: avoid !important;
              break-after: avoid !important;
            }
            
            /* Keep card content together */
            .space-y-4.p-4.pt-0 {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Avoid orphaned headings */
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid !important;
              break-after: avoid !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Keep list items together when possible */
            li {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Keep content blocks together */
            .bg-gray-50,
            .p-3.bg-gray-50.rounded-lg.border.border-gray-200 {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Ensure field content stays together */
            .text-sm > div,
            .flex.items-center.gap-2,
            .flex.items-center.gap-3 {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Ensure paragraphs don't get orphaned */
            p {
              orphans: 2;
              widows: 2;
            }
            
            /* Keep separators with their content */
            .mt-3 {
              page-break-before: avoid !important;
              break-before: avoid !important;
            }
          }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        ${content}
      </body>
    </html>
  `;
}

/**
 * Generate HTML template for printing multiple bills with page breaks
 */
export function generateBulkPrintHTML(bill_contents: { title: string; content: string }[]): string {
  const combined_content = bill_contents.map((bill, index) => {
    const page_break_style = index > 0 ? 'page-break-before: always !important; break-before: page !important;' : '';
    return `
      <div style="${page_break_style}">
        <h1>${bill.title}</h1>
        ${bill.content}
      </div>
    `;
  }).join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Legislative Reviews - Bulk Print</title>
        <style>
          body {
            font-family: system-ui, -apple-system, sans-serif;
            margin: 2rem;
            color: #111827;
            background: white;
            line-height: 1.5;
            min-height: 100vh;
            box-sizing: border-box;
          }
          
          html, body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
          }
          
          body {
            padding: 2rem;
          }
          h1, h2, h3, h4 {
            color: #111827;
            margin-bottom: 0.5rem;
          }
          h1 {
            font-size: 1.5rem;
            border-bottom: 2px solid #000;
            padding-bottom: 0.5rem;
            margin-bottom: 1rem;
          }
          .space-y-6 > * + * {
            margin-top: 1.5rem;
          }
          .space-y-4 > * + * {
            margin-top: 1rem;
          }
          .space-y-3 > * + * {
            margin-top: 0.75rem;
          }
          .space-y-2 > * + * {
            margin-top: 0.5rem;
          }
          .shadow-sm {
            border: 1px solid #ddd;
            border-radius: 0.5rem;
            padding: 1rem;
            margin-bottom: 1rem;
            background: white;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .bg-green-100 {
            background-color: #dcfce7;
            border: 1px solid #16a34a;
            color: #14532d;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            display: inline-block;
          }
          .bg-red-100 {
            background-color: #fee2e2;
            border: 1px solid #dc2626;
            color: #7f1d1d;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            display: inline-block;
          }
          .bg-blue-100 {
            background-color: #dbeafe;
            border: 1px solid #2563eb;
            color: #1e3a8a;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            display: inline-block;
          }
          .bg-gray-100 {
            background-color: #f3f4f6;
            border: 1px solid #6b7280;
            color: #374151;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            display: inline-block;
          }
          .bg-gray-50 {
            background-color: #f9fafb;
            border: 1px solid #d1d5db;
            padding: 0.75rem;
            border-radius: 0.5rem;
          }
          .text-muted-foreground {
            color: #6b7280;
          }
          .text-foreground {
            color: #111827;
          }
          ul, ol {
            padding-left: 1.5rem;
          }
          li {
            margin-bottom: 0.25rem;
          }
          @media print {
            @page {
              margin: 0.75in;
              size: A4;
            }
            
            /* Prevent sections (cards) from being split across pages */
            .shadow-sm,
            .rounded-xl,
            .shadow,
            [class*="shadow"] {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              page-break-before: auto;
              break-before: auto;
            }
            
            /* Ensure proper spacing between sections */
            .space-y-6 > *,
            .grid > * {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Keep card headers with their content */
            .flex.flex-col.space-y-1\\.5,
            .pb-3.p-4 {
              page-break-after: avoid !important;
              break-after: avoid !important;
            }
            
            /* Keep card content together */
            .space-y-4.p-4.pt-0 {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Avoid orphaned headings */
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid !important;
              break-after: avoid !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Keep list items together when possible */
            li {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Keep content blocks together */
            .bg-gray-50,
            .p-3.bg-gray-50.rounded-lg.border.border-gray-200 {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Ensure field content stays together */
            .text-sm > div,
            .flex.items-center.gap-2,
            .flex.items-center.gap-3 {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Ensure paragraphs don't get orphaned */
            p {
              orphans: 2;
              widows: 2;
            }
            
            /* Keep separators with their content */
            .mt-3 {
              page-break-before: avoid !important;
              break-before: avoid !important;
            }
            
            /* Force page breaks between bills */
            div[style*="page-break-before: always"] {
              page-break-before: always !important;
              break-before: page !important;
            }
          }
        </style>
      </head>
      <body>
        ${combined_content}
      </body>
    </html>
  `;
}
